import { useQuery } from '@tanstack/react-query';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/backend/lib/firebase';
import { Residence, RoomType } from '@/shared/types';
import { isDraft, isPublished } from '@/backend/lib/residenceUtils';

// This hook powers the PUBLIC residence/room detail pages only. A draft
// residence is treated as not-found, and draft rooms are hidden, so unpublished
// content is never reachable by a direct URL.
export const useResidence = (id: string | undefined) => {
  return useQuery({
    queryKey: ['residence', id],
    queryFn: async () => {
      if (!id) throw new Error('Residence ID is required');

      // Fetch residence
      const residenceDoc = await getDoc(doc(db, 'residences', id));
      if (!residenceDoc.exists()) throw new Error('Residence not found');

      const residence = {
        id: residenceDoc.id,
        ...residenceDoc.data()
      } as Residence;

      // A draft residence is not publicly viewable — surface it as not-found.
      if (isDraft(residence)) throw new Error('Residence not found');

      // Fetch associated room types
      const roomTypesRef = collection(db, 'roomTypes');
      const roomTypesQuery = query(roomTypesRef, where('residenceId', '==', id));
      const roomTypesSnapshot = await getDocs(roomTypesQuery);

      const roomTypes = (roomTypesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as RoomType[]).filter(isPublished);

      return { residence, roomTypes };
    },
    enabled: !!id
  });
};
